import * as React from 'react';
import folderStore from 'mini-mail-folder-store/lib/store/Store';
import getIsInCheckedMode from 'mini-mail-list-store/lib/utils/getIsInCheckedMode';
import getPreviewPaneViewState from 'mini-preview-pane-store/lib/selectors/getPreviewPaneViewState';
import isMiniFeatureEnabled from 'owa-mini-feature-flags/lib/isMiniFeatureEnabled';
import ListPane from 'mini-mail-list-view/lib/components/ListPane';
import MailHeader from 'mini-mail-header-view/lib/components/MailHeader';
import mailStore from 'owa-mail-store/lib/store/Store';
import Module from 'mini-common-schema/lib/schema/Module';
import ModuleSwitcher from 'mini-module-switcher/lib/components/ModuleSwitcher';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { A2HSCampaignProvider } from 'mini-a2hs/lib/components/A2HSCampaignProvider';
import { ActivityPane } from 'mini-activity-pane-view';
import { CalendarNudgeCampaign } from 'mini-mail-campaign-view/lib/components/CalendarNudgeCampaign';
import { CampaignName } from 'mini-campaign-bootstrap/lib/schema/MiniHostAppCampaign';
import { ComposeSource } from 'mini-mail-compose-store/lib/store/schema/ComposeSource';
import { composeStore, isFullComposeShown } from 'mini-mail-compose-store';
import { FeedbackForm } from 'mini-feedback';
import { FolderPane } from 'mini-mail-folder-view';
import { getMailRouteStore } from 'mini-mail-route-store/lib/store/MailRouteStore';
import { isActivityPaneShown } from 'mini-activity-pane-store/lib/selectors/isActivityPaneShown';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { isMiniHostAppCampaignEnabled } from 'mini-campaign-bootstrap/lib/utils/isMiniHostAppCampaignEnabled';
import { LazyAddAccountPane } from 'mini-add-account';
import { lazyDeleteComposeDraft } from 'mini-mail-compose-actions-mail';
import { LazyItemReadingPaneHost } from 'mini-mail-reading-pane-host';
import { LazyManageAccountsPageRoute } from 'multi-account';
import { LazyMiniMailCampaignSurface } from 'mini-mail-module-campaign';
import { lazySingleSelectActivityListItem } from 'mini-mail-activity-list-actions';
import { logUsage } from 'owa-analytics';
import { MailCompose } from 'mini-mail-compose-view';
import { mailEventEmitter, TRIGGER_MAIL_LIST_SCROLL_TO_TOP } from 'mini-mail-event-emitters';
import { MailModuleBanner } from './MailModuleBanner';
import { MailModuleDiagnostics } from 'mini-mail-diagnostics';
import { MailRouteType } from 'mini-mail-route-store/lib/schema/MailRouteType';
import { observer } from 'mobx-react';
import { OptionsPageRoute } from 'mini-options-view';
import { PeopleCardModuleWrapper } from 'mini-people-card-module-wrapper';
import { PeopleCardRouteStore } from 'mini-people-card-route-store';
import { PreviewPane } from 'mini-preview-pane-view';
import { ReadingPane } from 'mini-mail-reading-pane-view';
import { SearchModuleRoute } from 'mini-search-landing-view';
import { SurveyWrapper } from 'mini-survey';
import { targetedAdV3Dismissed } from 'mini-gdprv3-ads-opt-in/lib/utils/targetedAdUtils';
import { trace } from 'owa-trace';

const styles = require('./MailModule.scss');
const classNames = require('classnames/bind').bind(styles);

@observer
export default class MailModule extends React.Component<{}, {}> {
    private navigationButtonRef = React.createRef<HTMLElement>();

    private renderReadingPaneContainer(
        selectedId: string,
        isComposeShownOnTop: boolean
    ): JSX.Element {
        const readingPaneContainerClasses = classNames('readingPaneContainer', {
            'readingPaneContainer--shown': selectedId,
        });

        return (
            <div aria-hidden={isComposeShownOnTop} className={readingPaneContainerClasses}>
                {selectedId && <ReadingPane />}
            </div>
        );
    }

    /**
     * Return the component to be rendered based on the module selected.
     * TODO: Optimise this rendering as part of the react-router changes.
     * Each module should correspond to unique route
     */
    private getMailComponentToRender(): JSX.Element {
        let component: JSX.Element = null;

        switch (mailStore.mailModule.selectedModule) {
            case Module.Mail:
                component = this.renderMailModule();
                break;
            case Module.Search:
                component = this.renderSearchModule();
                break;
            case Module.Feedback:
                component = this.renderFeedbackModule();
                break;
            case Module.Options:
                component = this.renderOptionsModule();
                break;
            case Module.Survey:
                component = this.renderSurveyModule();
                break;
            case Module.Diagnostics:
                component = this.renderDiagnosticsModule();
                break;
            default:
                trace.warn('Invalid Module encountered.');
                component = null;
                break;
        }
        return component;
    }

    render() {
        const { routeType } = getMailRouteStore();

        // Determine the component to be rendered based on the routeType mini-mail-route-store.
        // Covers following distinct cases,
        // - reading pane deep-link scenario
        // - mail scenarios like compose, reading pane etc.
        const getChildren = () => {
            switch (routeType) {
                case MailRouteType.deeplinkReadItem:
                    return <LazyItemReadingPaneHost />;
                case MailRouteType.addAccount:
                    return <LazyAddAccountPane />;
                case MailRouteType.manageAccounts:
                    return <LazyManageAccountsPageRoute />;
                default:
                    return this.getMailComponentToRender();
            }
        };

        const suppressA2hsCampaign =
            !isMiniFeatureEnabled('mini-mail-a2hs-customPrompt') ||
            !isMiniHostAppCampaignEnabled(CampaignName.PWA);

        return (
            <A2HSCampaignProvider suppressCampaign={suppressA2hsCampaign}>
                {getChildren()}
            </A2HSCampaignProvider>
        );
    }

    private renderSearchModule(): JSX.Element {
        return <SearchModuleRoute />;
    }

    private renderMailModule(): JSX.Element {
        const { selectedId } = mailStore.mailModule;
        const isFullCompose = isFullComposeShown();
        const shouldRenderMailHeader = !isActivityPaneShown() || !!selectedId;
        const shouldRenderModuleSwitcher =
            !getIsInCheckedMode() &&
            !isFullCompose &&
            !selectedId &&
            !isInSearchView() &&
            !isActivityPaneShown();
        const { currentRoute, peopleCardMountProps } = PeopleCardRouteStore;

        const shouldShowMailComposeView =
            isFullCompose && composeStore.defaultViewState.source === ComposeSource.Mail;

        return (
            <div>
                {(folderStore.showFolderPane || folderStore.showMoveToFolderPane) && <FolderPane />}

                {/**
                 * This surface is for Mail Module Surface where all campaigns(tooltips, modals etc)
                 * pertaining to it will be rendered.
                 */}
                <LazyMiniMailCampaignSurface />

                {shouldRenderMailHeader && (
                    <MailHeader navigationButtonRef={this.navigationButtonRef} />
                )}

                <ListPane />

                {/* This component renders mutually exclusive banners which are to be shown in Mail module */}
                {isMiniHostAppCampaignEnabled(CampaignName.PWA) && (
                    <MailModuleBanner elementToFocusOnClose={this.navigationButtonRef} />
                )}

                {this.renderReadingPaneContainer(selectedId, shouldShowMailComposeView)}

                {shouldShowMailComposeView && (
                    <MailCompose customComposeDraftDelete={this.onComposeDraftDelete} />
                )}

                {shouldRenderModuleSwitcher && this.renderModuleSwitcher()}

                {!!currentRoute && !!currentRoute.routeIdType && (
                    <PeopleCardModuleWrapper emailAddress={peopleCardMountProps.emailAddress} />
                )}

                {getPreviewPaneViewState() && <PreviewPane />}

                {isMiniFeatureEnabled('mini-reactions') && isActivityPaneShown() && (
                    <ActivityPane
                        onActivityItemClick={this.onActivityItemClick}
                        isReadingPaneOpen={!!selectedId}
                    />
                )}
            </div>
        );
    }

    private onActivityItemClick = (itemId: string, conversationId: string, subject: string) => {
        lazySingleSelectActivityListItem.importAndExecute(itemId, conversationId, subject);
    };

    private onComposeDraftDelete = async (itemId: string) => {
        (await lazyDeleteComposeDraft.import())(itemId);
    };

    private renderFeedbackModule(): JSX.Element {
        return (
            <div>
                <FeedbackForm
                    containerModule={Module.Mail}
                    onCloseCallback={this.switchBackToMailModule}
                />
            </div>
        );
    }

    private renderOptionsModule(): JSX.Element {
        return <OptionsPageRoute />;
    }

    private renderSurveyModule(): JSX.Element {
        return <SurveyWrapper />;
    }

    private switchBackToMailModule = () => {
        targetedAdV3Dismissed();
        switchModule(Module.Mail);
    };

    private renderModuleSwitcher(): JSX.Element {
        const calendarIconRef = React.createRef<HTMLElement>();
        return (
            <>
                <ModuleSwitcher
                    currentModule={Module.Mail}
                    iconRefs={{ calendarIconRef }}
                    onActiveModuleClickAction={this.onActiveModuleClickAction}
                />
                <CalendarNudgeCampaign calendarIconRef={calendarIconRef} />
            </>
        );
    }

    private onActiveModuleClickAction() {
        mailEventEmitter.emit(TRIGGER_MAIL_LIST_SCROLL_TO_TOP, null);
        logUsage('ScrollToTopOnReClick', { source: 'MailModule' });
    }

    private renderDiagnosticsModule(): JSX.Element {
        return <MailModuleDiagnostics onDismiss={() => switchModule(Module.Mail)} />;
    }
}
